import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import AboutWasslaModal from '../components/AboutWasslaModal';
import AppHeader from '../components/AppHeader';
import WasslaLogo from '../components/WasslaLogo';
import './Settings.css';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isDarkMode, toggleTheme } = useTheme();
  const [showAbout, setShowAbout] = useState(false);

  const isRTL = i18n.language?.startsWith('ar') || document.dir === 'rtl';

  const rows = useMemo(() => [
    {
      key: 'language',
      label: t('sett.language'),
      value: t('sett.english'),
      onClick: () => {
        const next = isRTL ? 'en' : 'ar';
        i18n.changeLanguage(next);
        localStorage.setItem('wassla_lang', next);
        localStorage.setItem('lang', next);
        document.documentElement.dir = next === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = next;
      },
    },
    {
      key: 'theme',
      label: t('sett.theme'),
      value: isDarkMode ? (t('sett.dark')) : (t('sett.light')),
      onClick: toggleTheme,
    },
    {
      key: 'notifications',
      label: isRTL ? 'إعدادات الإشعارات' : 'Notifications',
      value: '',
      onClick: () => navigate('/settings/notifications'),
    },
    {
      key: 'profile',
      label: t('sett.editProfile'),
      value: '',
      onClick: () => navigate('/account'),
    },
    {
      key: 'privacy',
      label: t('cont.privacyPolicy'),
      value: '',
      onClick: () => navigate('/privacy'),
    },
    {
      key: 'terms',
      label: t('sett.termsConditions'),
      value: '',
      onClick: () => navigate('/terms'),
    },
    // iter306 — explicit delete-account row (Play / App Store requirement).
    {
      key: 'delete-account',
      label: isRTL ? 'حذف الحساب' : 'Delete account',
      value: '',
      onClick: () => navigate('/delete-account'),
    },
    {
      key: 'about',
      label: t('sett.aboutWassla'),
      value: '',
      onClick: () => setShowAbout(true),
    },
  ], [isRTL, i18n, navigate, toggleTheme, isDarkMode]);

  return (
    <div className="settings-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="settings-page">
      {/* Shared AppHeader — Material-style back + title. */}
      <AppHeader
        backFallback="/account"
        testId="settings-header"
        titleSlot={
          <>
            <h1 className="app-header__title" data-testid="settings-page-title">
              {t('more.settings')}
            </h1>
            <p className="app-header__subtitle">
              {t('sett.manageYourAppAndAccount')}
            </p>
          </>
        }
      />
      <div className="settings-page-inner">
        <section className="settings-list" data-testid="settings-list">
          {rows.map((row) => (
            <button
              key={row.key}
              type="button"
              className="settings-row"
              onClick={row.onClick}
              data-testid={`settings-row-${row.key}`}
            >
              <span>{row.label}</span>
              <span className="settings-row-value">{row.value || (isRTL ? '←' : '→')}</span>
            </button>
          ))}
        </section>

        {/* Footer with Wassla Logo */}
        <div className="settings-footer" data-testid="settings-footer">
          <WasslaLogo size="footer" />
          <span className="settings-footer-copy">
            {isRTL
              ? `© ${new Date().getFullYear()} وصلة`
              : `© ${new Date().getFullYear()} Wassla`}
          </span>
        </div>
      </div>

      <AboutWasslaModal isOpen={showAbout} onClose={() => setShowAbout(false)} />
    </div>
  );
}
